import { useEffect, useRef, useState } from 'react';
import { FILL_TRANSITION_DURATION } from '../../../ProgressBar/constants';

/** All durations are in seconds. */
export interface FakeProgressConfig {
  totalDuration: number;
  rapidTotalDuration: number;
  creepStartAt: number;
  creepDuration: number;
}

export interface FakeProgress {
  currentStep: number; // Ranges from 0 to totalSteps - 1.
  progress: number; // Progress ranges from 0 to 100.
}

const NORMAL_TICK_DELAY = 0.2;
const RAPID_TICK_DELAY = 0.03;
const CREEP_CEILING = 99; // Progress never exceeds 99% while waiting for isComplete

const DEFAULT_CONFIG: FakeProgressConfig = {
  totalDuration: 90,
  rapidTotalDuration: 5,
  creepStartAt: 88,
  creepDuration: 120,
};

/**
 * Captures startTime once per step so elapsed time is always relative to when the step began.
 * Without a factory, startTime would reset on every tick call → elapsed always ≈ 0 → progress never advances.
 */
const createProgressCalculator = (
  stepStart: number,
  duration: number,
  cap: number
) => {
  const startTime = Date.now();

  return (current: number): number => {
    const elapsed = Date.now() - startTime;
    const range = cap - stepStart;
    const completionFraction = duration > 0 ? elapsed / duration : 1;
    const elapsedProgress = stepStart + completionFraction * range;
    const randomizedProgress = Math.max(
      current,
      elapsedProgress + Math.random() * range * 0.1
    );
    const nextProgress = Math.min(randomizedProgress, cap);
    return nextProgress;
  };
};

const generateStepDurations = (
  totalSteps: number,
  totalDuration: number
): number[] => {
  const stepDuration = totalDuration / totalSteps;
  return Array.from({ length: totalSteps }, () => stepDuration);
};

/**
 * Drives a multi-step fake progress animation designed to pair with ProgressBar.
 *
 * Progress flows continuously from 0–100% across all steps.
 * Steps advance automatically at a natural pace, then creep slowly toward 99% while waiting.
 * When isComplete becomes true the animation switches to rapid mode and races to 100%.
 */
export const useFakeProgress = (
  totalSteps: number,
  isComplete: boolean,
  config?: Partial<FakeProgressConfig>
): FakeProgress => {
  const { totalDuration, rapidTotalDuration, creepStartAt, creepDuration } = {
    ...DEFAULT_CONFIG,
    ...config,
  };

  const [currentStep, setCurrentStep] = useState(0);
  const [progress, setProgress] = useState(0);

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const progressRef = useRef(0);
  const durationsRef = useRef<number[]>(
    generateStepDurations(totalSteps, totalDuration)
  );

  const isLastStep = currentStep === totalSteps - 1;
  const lastStepStart = ((totalSteps - 1) / totalSteps) * 100;
  /** Constrain creepStartAt so range = cap - stepStart is never negative in createProgressCalculator.
   * Otherwise it produces a negative range, which causes progress to jump backwards. */
  const safeCreepStartAt = Math.min(
    Math.max(creepStartAt, lastStepStart),
    CREEP_CEILING
  );

  useEffect(() => {
    const normalDuration = durationsRef.current[currentStep] * 1000;
    const rapidDuration = (rapidTotalDuration / totalSteps) * 1000;
    const stepStart = (currentStep / totalSteps) * 100;
    const stepEnd = ((currentStep + 1) / totalSteps) * 100;

    const cap = isLastStep && !isComplete ? safeCreepStartAt : stepEnd;
    const duration = isComplete ? rapidDuration : normalDuration;
    const tickDelay = isComplete
      ? RAPID_TICK_DELAY * 1000
      : NORMAL_TICK_DELAY * 1000;

    const progressCalculator = createProgressCalculator(
      stepStart,
      duration,
      cap
    );

    const tick = () => {
      const nextProgress = progressCalculator(progressRef.current);
      progressRef.current = nextProgress;
      setProgress(nextProgress);

      if (nextProgress < cap) {
        timerRef.current = setTimeout(tick, tickDelay);
        return;
      }

      if (isLastStep && !isComplete) {
        const creepCalculator = createProgressCalculator(
          safeCreepStartAt,
          creepDuration * 1000,
          CREEP_CEILING
        );
        const creepTick = () => {
          const next = creepCalculator(progressRef.current);
          progressRef.current = next;
          setProgress(next);
          if (next < CREEP_CEILING) {
            timerRef.current = setTimeout(creepTick, NORMAL_TICK_DELAY * 1000);
          }
        };
        timerRef.current = setTimeout(creepTick, NORMAL_TICK_DELAY * 1000);
        return;
      }

      if (!isLastStep) {
        timerRef.current = setTimeout(() => {
          const nextStepStart = ((currentStep + 1) / totalSteps) * 100;
          progressRef.current = nextStepStart;
          setProgress(nextStepStart);
          setCurrentStep(currentStep + 1);
        }, FILL_TRANSITION_DURATION * 1000);
      }
    };

    timerRef.current = setTimeout(tick, tickDelay);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [currentStep, isComplete]);

  return { currentStep, progress };
};
